import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { Hashtag } from "../models/hashtag";
import { HashtagPerReport } from "../models/hashtagPerReport";
import { HashtagPerWidget } from "../models/hashtagPerWidget";
import { DecimalPipe } from "@angular/common";
import { AppServiceHelperService } from "../../../../services/app-service-helper.service";
import { ReportDesignerStorageService } from "../../services/report-designer-storage.service";
import { catchError, concatMap, switchMap } from "rxjs/operators";
import { WidgetService } from "../../widgets/widget.service";
import { ReportDataPreloaderService } from "../../services/print/report-data-preloader-service";

/**
 * Service used for hashtag logic
 */
@Injectable({
  providedIn: 'root'
})
export class HashtagService {
  /**
   * Api for widgets
   */
  private _widgetApi = 'reportwidgets';

  /**
   * Api for hashtags
   */
  private _hashtagsApi = 'hashtags';

  /**
   * Widget Resource
   */
  private _widgetResource: string;

  /**
   * BehaviorSubject for hashtag, when hashtag is selected for widget. Used in hashtag directive
   */
  public selectedHashtag$: BehaviorSubject<HashtagPerWidget> = new BehaviorSubject<HashtagPerWidget>(null);

  /**
   * All selected hashtags in current report
   */
  private allSelectedHashTags: HashtagPerReport[] = [];

  /**
   * BehaviorSubject for all selected hashtags  in current report
   */
  public allSelectedHashTags$: BehaviorSubject<HashtagPerReport[]> = new BehaviorSubject<HashtagPerReport[]>(null);

  /**
   * All already loaded tags.
   */
  private loadedHashTags: Hashtag[] = [];

  /**
   * Event emitter when hashtags are retrieved . Used in hashtags-per-widget component
   */
  public loadedHashTags$: BehaviorSubject<Hashtag[]> = new BehaviorSubject<Hashtag[]>(null);

  /**
   * serach key
   */
  private searchKey: string;

  /**
   * BehaviorSubject for the search key
   */
  public searchKey$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  /**
   * Are hashtags loaded
   */
  private hashTagsLoaded = false;

  /**
   * CURRENCY ON REPORT LEVEL
   */
  private currency: string;

  /**
   * UNIT OF LENGTH ON REPORT LEVEL
   */
  private unitOfLength: string;

  /**
   * UNIT OF AREA ON REPORT LEVEL
   */
  private unitOfArea: string;

  /**
   * Constructor
   * @param rdStorage
   * @param httpClient
   * @param decimalPipe
   * @param appServiceHelper
   * @param widgetService
   * @param reportDataPreloaderService
   */
  constructor(private rdStorage: ReportDesignerStorageService,
    private httpClient: HttpClient,
    private decimalPipe: DecimalPipe,
    private appServiceHelper: AppServiceHelperService,
    private widgetService: WidgetService,
    private reportDataPreloaderService: ReportDataPreloaderService) {
    this._widgetResource = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', this._hashtagsApi);
    this.currency = this.rdStorage.getCurrencyDescription();
    this.unitOfLength = this.rdStorage.getUnitofLength();
    this.unitOfArea = `${this.unitOfLength}²`;
  }

  /**
   * Add hashtag to the list. Used to display all hashtags on report level
   */
  public addHashtagToReportList(hashtag: Hashtag, pageNum: number, widgetId: number) {
    const hashtagOverview = new HashtagPerReport();
    hashtagOverview.dto = hashtag.dto;
    hashtagOverview.key = hashtag.key;
    hashtagOverview.property = hashtag.property;
    hashtagOverview.reflist_name = hashtag.reflist_name;
    hashtagOverview.page = pageNum;
    hashtagOverview.value = hashtag.value;
    hashtagOverview.widgetId = widgetId;
    this.allSelectedHashTags.push(hashtagOverview);
    this.allSelectedHashTags$.next(this.allSelectedHashTags);
  }

  /**
   * Format hashtag value based on dataverse flags
   */
  public formatHashtagValue(value: string, hashtag: Hashtag): string {
    if (value === null || value === undefined) {
      return '';
    }
    this.checkIfMeasurmentsAreMapped();

    if (hashtag.isPercentage) {
      return `${this.decimalPipe.transform(+value * 100, '1.0-2')}%`
    } else if (hashtag.isCurrency) {
      return `${this.currency}${this.decimalPipe.transform(+value, '1.0-2')}`;
    } else if (hashtag.isLength) {
      return `${this.decimalPipe.transform(+value, '1.0-2')}${this.unitOfLength}`;
    } else if (hashtag.isArea) {
      return `${this.decimalPipe.transform(+value, '1.0-2')}${this.unitOfArea}`;
    } else if (this.isDecimalNumber(value)) {
      return `${this.decimalPipe.transform(+value, '1.0-2')}`;
    } else {
      return value;
    }
  }

  /**
   * Check if measurement units are mapped
   */
  private checkIfMeasurmentsAreMapped() {
    if (!this.currency)
      this.currency = this.rdStorage.getCurrencyDescription();
    if (!this.unitOfLength) {
      this.unitOfLength = this.rdStorage.getUnitofLength();
      this.unitOfArea = `${this.unitOfLength}²`;
    }
  }

  /**
   * Is the value a decimal number?
   * @param value
   * @returns
   */
  private isDecimalNumber(value: any): boolean {
    if (typeof value === "number" && !Number.isNaN(value)) {
      return !Number.isInteger(value);
    }
    return false;
  }

  /**
   * Set value for search key
   * @param key
   */
  public setSearchKey(key: string) {
    this.searchKey = key;
    this.searchKey$.next(this.searchKey);
  }

  /**
   * Remove hashtags for widget from hashtag list
   * @param widgetId
   * @param key
   * @param page
   */
  public removeSelectedHashtag(widgetId: number, key: string, page: number) {
    const selectedTag = this.allSelectedHashTags.find(ht => ht.widgetId === widgetId && ht.key === key && ht.page === page);
    if (selectedTag) {
      this.allSelectedHashTags.splice(this.allSelectedHashTags.indexOf(selectedTag), 1);
    }

    this.allSelectedHashTags$.next(this.allSelectedHashTags);
  }

  /**
   * Emits values for all selected hashtags
   */
  public getHashTagsForReport() {
    this.allSelectedHashTags$.next(this.allSelectedHashTags);
  }

  /**
   * Emiting new value for selected hashtag
   * @param hashtag
   */
  public applyHashtagToWidget(hashtag: HashtagPerWidget) {
    this.selectedHashtag$.next(hashtag);
  }

  /**
   * Clear value for selected hashtag
   */
  public clearSelectedHashtag() {
    this.selectedHashtag$.next(null);
  }

  /**
   * Gets hashtags. If data is already loaded, it will be used from  loadedHashtagsPerWidget, otherwise API for retrieving data will be called.
   */
  public getAllHashtags() {
    if (this.hashTagsLoaded) {
      this.loadedHashTags$.next(this.loadedHashTags);
    } else {
      this.hashTagsLoaded = true;//TODO: flag set too early (before actual call ends?)?
      this.loadAllHashtags().subscribe(data => {
        this.loadedHashTags = data;
        this.loadedHashTags$.next(this.loadedHashTags);
      });
    }
  }

  /**
   * Call API service for retrieveing hashtag value, based on DTO
   * @param key
   * @returns
   */
  public getHashtagValue(key: string): Observable<any> {
    const selectedHashtag = this.loadedHashTags.find(h => h.key === key);
    const url = `${this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', this._widgetApi)}/${selectedHashtag.dto.toLowerCase().replace('widget', '')}?id=${this.rdStorage.getReport()}`;
    const preloadedData = this.reportDataPreloaderService.getDataForUrl(url);
    return preloadedData ? of(preloadedData) : this.httpClient.get(url);
  }

  /**
   * Retrieves hashtag by key
   * @param key
   * @returns
   */
  public getHashtagDto(key: string): Hashtag {
    return this.loadedHashTags.find(h => h.key === key);
  }

  /**
   * Retrieves hashtag details. If details are not already loaded, api is called
   * @param key
   * @returns
   */
  public getHashtagDtoDetails(key: string): Observable<Hashtag> {
    const hashTag = this.loadedHashTags.find(h => h.key === key);
    if (hashTag) {
      if (hashTag.dto) {
        return of(hashTag);
      } else {
        return this.loadHashtagDetails(hashTag.id);
      }
    } else {
      return of(null)
    }
  }

  /**
   * Set value for hashtag
   * @param key
   * @param value
   */
  public setHashtagValue(key: string, value: string): void {
    const hashtagDetails = this.loadedHashTags.find(h => h.key === key);
    if ((value === null || value === undefined) &&
      (hashtagDetails.type === 'Int' || hashtagDetails.type === 'Decimal' || hashtagDetails.type === 'SmallInt')) {
      value = '0';
    }
    hashtagDetails.value = value;
    hashtagDetails.isValueLoaded = true;
  }

  /**
   * Set all additional details for hashtag
   * @param key
   * @param details
   */
  public setHashtagDetails(key: string, details: Hashtag): void {
    const hashtagDetails = this.loadedHashTags.find(h => h.key === key);
    hashtagDetails.dto = details.dto;
    hashtagDetails.property = details.property;
    hashtagDetails.isArea = details.isArea;
    hashtagDetails.isLength = details.isLength;
    hashtagDetails.isCurrency = details.isCurrency;
    hashtagDetails.isPercentage = details.isPercentage;
    hashtagDetails.reflist_name = details.reflist_name;
    hashtagDetails.value = details.value;
    hashtagDetails.type = details.type;
  }


  /**
   * API for loading hashtags for widget
   * @returns
   */
  public loadAllHashtags(): Observable<Hashtag[]> {
    return this.httpClient.get<Hashtag[]>(`${this._widgetResource}/list`);
  }

  /**
   * API for loading hashtag details for widget
   * @param id
   * @returns
   */
  public loadHashtagDetails(id: number): Observable<Hashtag> {
    return this.httpClient.get<Hashtag>(`${this._widgetResource}/details?id=${id}`);
  }

  /**
   * Create Observables for the hashtag details
   * @param hashtags
   * @param pageNum
   * @param widgetId
   * @returns
   */
  public createHashtagDetailsObservables(hashtags: any[], pageNum: number, widgetId: number) {
    const hashtagObservables = hashtags.map(h => {
      const key = h.replace('#=', '').replace('=#', '');
      return this.getHashtagDtoDetails(key).pipe(
        concatMap(hashtagDetails => {
          if (hashtagDetails) {
            this.setHashtagDetails(key, hashtagDetails);
            // if details were not loaded, load them
            if (!hashtagDetails.isValueLoaded) {
              return this.loadData(key, hashtagDetails, widgetId, pageNum);
            } else {
              // If the hashtag details are already loaded, return them without further processing
              return of(hashtagDetails);
            }
          } else {
            // If there are no hashtag details, return an observable with a default value or handle it accordingly
            return of(null);
          }
        }));
    });

    return hashtagObservables;
  }

  /**
   * If details were not loaded, load them
   * @param key
   * @param hashtagDetails
   * @param widgetId
   * @param pageNum
   * @returns
   */
  private loadData(key: string, hashtagDetails: Hashtag, widgetId: number, pageNum: number): Observable<Hashtag> {
    const property = hashtagDetails.property;
    let hashtagValue: string;
    return this.getHashtagValue(key).pipe(
      switchMap(hashtagValueData => {
        // if selected hashtag has value, apply it
        if (hashtagValueData[property] !== undefined && hashtagValueData[property] !== null) {
          hashtagValue = hashtagValueData[property];
          // if hashtag has a reference to a ref list, call the API for the ref list
          if (hashtagDetails.reflist_name) {
            return this.handleReflist(hashtagDetails, hashtagValue, key, widgetId, pageNum);
          } else {

            return this.handleNormalHashtag(key, hashtagValue, hashtagDetails, widgetId, pageNum);
          }
        } else {
          // If the hashtag value is not set, check if there is secondary field for hashtag and if it contains value
          if (hashtagDetails.secondaryField !== undefined && hashtagDetails.secondaryField !== null &&
            hashtagValueData[hashtagDetails.secondaryField] !== undefined && hashtagValueData[hashtagDetails.secondaryField] !== null) {
            hashtagValue = hashtagValueData[hashtagDetails.secondaryField]
          }
          this.setHashtagValue(key, hashtagValue);
          hashtagDetails.value = hashtagValue;
          if (widgetId > 0) {
            this.addHashtagToReportList(hashtagDetails, pageNum, widgetId);
          }
          this.clearSelectedHashtag();
          return of(hashtagDetails);
        }
      }));
  }

  /**
   * Handles a normal hashtag
   * @param key
   * @param hashtagValue
   * @param hashtagDetails
   * @param widgetId
   * @param pageNum
   * @returns
   */
  private handleNormalHashtag(key: any, hashtagValue: string, hashtagDetails: Hashtag, widgetId: number, pageNum: number): Observable<Hashtag> {
    this.setHashtagValue(key, hashtagValue);
    hashtagDetails.value = hashtagValue;
    if (widgetId > 0) {
      this.addHashtagToReportList(hashtagDetails, pageNum, widgetId);
    }
    this.clearSelectedHashtag();
    return of(hashtagDetails);
  }

  /**
   *  if hashtag has a reference to a ref list, call the API for the ref list
   * @param hashtagDetails
   * @param hashtagValue
   * @param key
   * @param widgetId
   * @param pageNum
   * @returns
   */
  private handleReflist(hashtagDetails: Hashtag, hashtagValue: string, key: any, widgetId: number, pageNum: number): Observable<Hashtag> {
    return this.widgetService.loadReflist(hashtagDetails.reflist_name).pipe(
      catchError(error => {
        // Handle the error if the reflist API call fails
        console.error('Error loading reflist:', error);
        return of(null); // Return a fallback value or null
      }),
      switchMap((data: any[]) => {
        const refListSelectedValue = data.find(d => d.oid === +hashtagValue);
        if (refListSelectedValue) {
          hashtagValue = refListSelectedValue.description;
          this.setHashtagValue(key, hashtagValue);
          hashtagDetails.value = hashtagValue;
          if (widgetId > 0) {
            this.addHashtagToReportList(hashtagDetails, pageNum, widgetId);
          }
          this.clearSelectedHashtag();
        }
        return of(hashtagDetails);
      })
    );
  }
}
